import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkles, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Each step targets a data-testid OR renders a centered welcome modal (target=null).
const STEPS = [
  {
    id: "welcome",
    target: null,
    title: "Welcome to InvoiceSetu",
    body: "Let's take a 30-second tour to show you the key features. You can skip anytime.",
    placement: "center",
    requiresPath: "/dashboard",
  },
  {
    id: "checklist",
    target: '[data-testid="onboarding-checklist"]',
    title: "Your onboarding checklist",
    body: "Track setup progress here. Each step unlocks the next part of your invoicing workflow — your CA can fill the rest in for you.",
    placement: "bottom",
    requiresPath: "/dashboard",
  },
  {
    id: "stats",
    target: '[data-testid="dashboard-stats"]',
    title: "Live business overview",
    body: "Pending invoices, overdue, total receivables, monthly sales & purchases — all updated the moment you log a transaction.",
    placement: "bottom",
    requiresPath: "/dashboard",
  },
  {
    id: "create",
    target: '[data-testid="dashboard-create-btn"]',
    title: "Create an invoice in 60 seconds",
    body: "Pick a customer, add line items (auto-fills from your products), apply GST — done. Or use Analyze to OCR a PDF/image bill.",
    placement: "bottom",
    requiresPath: "/dashboard",
  },
  {
    id: "find-ca",
    target: '[data-testid="nav-link-find-ca"]',
    title: "Connect your CA",
    body: "Browse verified CAs in your city, send a connect request, and let them manage GST filings without compromising your data.",
    placement: "bottom",
    requiresPath: "/dashboard",
    fallbackTarget: '[data-testid="onboarding-step-ca_connected"]',
  },
];

export default function GuidedTour() {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [windowSize, setWindowSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const navigate = useNavigate();
  const location = useLocation();
  const checkedRef = useRef(false);

  // Decide whether to start the tour after login on dashboard
  useEffect(() => {
    if (checkedRef.current) return;
    if (location.pathname !== "/dashboard") return;
    checkedRef.current = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/onboarding/state`, { withCredentials: true });
        if (!res.data.has_seen_tour) {
          // small delay so dashboard finishes its mount animations
          setTimeout(() => setActive(true), 600);
        }
      } catch { /* silent */ }
    })();
  }, [location.pathname]);

  // Listen to global "replay-tour" event
  useEffect(() => {
    const onReplay = () => {
      setStepIdx(0);
      if (location.pathname !== "/dashboard") {
        navigate("/dashboard");
        setTimeout(() => setActive(true), 800);
      } else {
        setActive(true);
      }
    };
    window.addEventListener("invoicesetu:replay-tour", onReplay);
    return () => window.removeEventListener("invoicesetu:replay-tour", onReplay);
  }, [location.pathname, navigate]);

  // Track window resize + scroll for re-anchoring spotlight
  useEffect(() => {
    const onResize = () => setWindowSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, []);

  // Recompute target rect whenever step changes / window changes
  useLayoutEffect(() => {
    if (!active) return;
    const step = STEPS[stepIdx];
    if (!step?.target) {
      setRect(null);
      return;
    }
    let el = document.querySelector(step.target);
    if (!el && step.fallbackTarget) el = document.querySelector(step.fallbackTarget);
    if (!el) {
      setRect(null);
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // wait for scroll to finish before measuring
    const t = setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, 350);
    return () => clearTimeout(t);
  }, [active, stepIdx, windowSize, location.pathname]);

  const finish = async (skipped) => {
    setActive(false);
    setStepIdx(0);
    try {
      await axios.post(`${API}/onboarding/seen-tour`, {}, { withCredentials: true });
    } catch { /* silent */ }
    if (!skipped) {
      // gentle confetti-free celebration toast omitted; subtle is better
    }
  };

  const next = () => {
    if (stepIdx >= STEPS.length - 1) finish(false);
    else setStepIdx((i) => i + 1);
  };
  const prev = () => setStepIdx((i) => Math.max(0, i - 1));

  if (!active) return null;
  const step = STEPS[stepIdx];

  // Compute tooltip position
  const PAD = 8;
  let tooltipStyle = {};
  if (rect && step.placement !== "center") {
    const tooltipW = Math.min(360, windowSize.w - 32);
    const tooltipH = 180; // estimated
    let top = rect.top + rect.height + PAD + 6;
    let left = rect.left + rect.width / 2 - tooltipW / 2;

    // Flip to top if not enough room below
    if (top + tooltipH > windowSize.h - 16) {
      top = rect.top - tooltipH - PAD - 6;
    }
    // Clamp left
    left = Math.max(16, Math.min(left, windowSize.w - tooltipW - 16));
    tooltipStyle = { top, left, width: tooltipW };
  } else {
    tooltipStyle = {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: Math.min(420, windowSize.w - 32),
    };
  }

  return (
    <AnimatePresence>
      <motion.div
        key="tour-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] pointer-events-none"
        data-testid="guided-tour"
      >
        {/* Spotlight: 4 dark overlays around the target rect (avoids SVG mask portability issues) */}
        {rect ? (
          <>
            <div className="absolute bg-slate-900/65 pointer-events-auto" style={{ top: 0, left: 0, right: 0, height: Math.max(0, rect.top - PAD) }} onClick={() => finish(true)} />
            <div className="absolute bg-slate-900/65 pointer-events-auto" style={{ top: rect.top - PAD, left: 0, width: Math.max(0, rect.left - PAD), height: rect.height + PAD * 2 }} onClick={() => finish(true)} />
            <div className="absolute bg-slate-900/65 pointer-events-auto" style={{ top: rect.top - PAD, left: rect.left + rect.width + PAD, right: 0, height: rect.height + PAD * 2 }} onClick={() => finish(true)} />
            <div className="absolute bg-slate-900/65 pointer-events-auto" style={{ top: rect.top + rect.height + PAD, left: 0, right: 0, bottom: 0 }} onClick={() => finish(true)} />
            {/* Spotlight ring */}
            <div
              className="absolute rounded-2xl pointer-events-none ring-4 ring-blue-400/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.0)] transition-all duration-300"
              style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }}
            />
          </>
        ) : (
          <div className="absolute inset-0 bg-slate-900/65 pointer-events-auto" onClick={() => finish(true)} />
        )}

        {/* Tooltip card */}
        <motion.div
          key={`tour-card-${stepIdx}`}
          initial={{ opacity: 0, y: 8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ duration: 0.22 }}
          className="absolute pointer-events-auto bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200/60"
          style={tooltipStyle}
          data-testid={`tour-step-${step.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between p-4 pb-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500 text-white flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Step {stepIdx + 1} / {STEPS.length}</p>
                <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{step.title}</h3>
              </div>
            </div>
            <button onClick={() => finish(true)} className="text-slate-400 hover:text-slate-600 flex-shrink-0" data-testid="tour-close" aria-label="Close tour">
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="text-sm text-slate-600 px-4 pb-3 leading-relaxed">{step.body}</p>

          {/* Progress dots */}
          <div className="flex items-center gap-1 px-4 pb-3" data-testid="tour-progress-dots">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-300 ${i === stepIdx ? "w-6 bg-blue-500" : i < stepIdx ? "w-3 bg-blue-300" : "w-3 bg-slate-200"}`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
            <button
              onClick={() => finish(true)}
              className="text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
              data-testid="tour-skip"
            >
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              {stepIdx > 0 && (
                <button
                  onClick={prev}
                  className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 border border-slate-200 text-slate-700 hover:bg-white transition-colors"
                  data-testid="tour-prev"
                >
                  <ArrowLeft className="w-3 h-3" /> Back
                </button>
              )}
              <button
                onClick={next}
                className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                data-testid="tour-next"
              >
                {stepIdx >= STEPS.length - 1 ? (<>Got it <CheckCircle2 className="w-3 h-3" /></>) : (<>Next <ArrowRight className="w-3 h-3" /></>)}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
